import type {
	EditorState,
	ReadonlyTransaction,
	Transaction,
} from '@atlaskit/editor-prosemirror/state';

import type { AgentManagedExtensionPluginState } from '../../common/agentManagedExtensionPluginType';
import { findAgentManagedExtension } from '../../common/utils';

export type AgentManagedExtensionMeta =
	| { type: 'OPEN_CONFIG' }
	| { type: 'CLOSE_CONFIG' }
	| { type: 'TOGGLE_CONFIG' };

const META_KEY = 'agentManagedExtension';

export const setMeta = (tr: Transaction, meta: AgentManagedExtensionMeta): Transaction =>
	tr.setMeta(META_KEY, meta);

export const getMeta = (
	tr: Transaction | ReadonlyTransaction,
): AgentManagedExtensionMeta | undefined =>
	tr.getMeta(META_KEY) as AgentManagedExtensionMeta | undefined;

export const initialState = (): AgentManagedExtensionPluginState => ({
	isConfigOpen: false,
});

/**
 * Pure reducer for AgentManagedExtensionMeta actions.
 */
export const applyMeta = (
	prev: AgentManagedExtensionPluginState,
	meta: AgentManagedExtensionMeta | undefined,
): AgentManagedExtensionPluginState => {
	if (!meta) {
		return prev;
	}
	switch (meta.type) {
		case 'OPEN_CONFIG':
			return { isConfigOpen: true };
		case 'CLOSE_CONFIG':
			return { isConfigOpen: false };
		case 'TOGGLE_CONFIG':
			return { isConfigOpen: !prev.isConfigOpen };
		default:
			return prev;
	}
};

/**
 * Returns `{ isConfigOpen: false }` when the popup is open but the
 * selection has moved out of any agent-managed extension. Otherwise
 * returns `prev` unchanged.
 */
export const applyAutoCloseOnSelectionChange = (
	prev: AgentManagedExtensionPluginState,
	tr: Transaction | ReadonlyTransaction,
	newState: EditorState,
): AgentManagedExtensionPluginState => {
	if (!prev.isConfigOpen || !tr.selectionSet) {
		return prev;
	}
	if (findAgentManagedExtension(newState)) {
		return prev;
	}
	return { isConfigOpen: false };
};
